import type { FC, ReactNode } from 'react'
import type { GetStaticProps } from 'next'
import type {
    Client,
    GalleryItem,
    Service,
    Testimonial
} from '@/types'

import { safely } from '@/lib/db'
import { Seo } from '@/components/generals/seo'
import { Layout } from '@/components/generals/layout'
import { CtaBanner } from '@/components/generals/cta-banner'
import { ClientsMarquee } from '@/components/generals/clients-marquee'
import { Hero } from '@/components/landing/hero'
import { Highlights } from '@/components/landing/highlights'
import { AboutTeaser } from '@/components/landing/about-teaser'
import { ServicesShowcase } from '@/components/landing/services-showcase'
import { WhyChoose } from '@/components/landing/why-choose'
import { GalleryPreview } from '@/components/landing/gallery-preview'
import { TestimonialsSection } from '@/components/landing/testimonials-section'

import {
    getClients,
    getGalleryItems,
    getServices,
    getTestimonials
} from '@/lib/content'

interface HomeProps {
    services: Service[]
    gallery: GalleryItem[]
    testimonials: Testimonial[]
    clients: Client[]
}

const Home: FC<HomeProps> = ({
    services,
    gallery,
    testimonials,
    clients
}): ReactNode => {
    const [spotlight, ...otherTestimonials] = testimonials

    return (
        <Layout>
            <Seo description='Guju Events plans and manages luxury weddings, corporate events, garba nights, concerts, sports events and manpower from Ahmedabad, across India.' />

            <Hero clients={clients} />
            <Highlights brandCount={clients.length} />
            <AboutTeaser testimonial={spotlight ?? null} />
            <ServicesShowcase services={services} />
            <WhyChoose />
            <GalleryPreview items={gallery} />
            <ClientsMarquee clients={clients} />
            <TestimonialsSection testimonials={otherTestimonials} />
            <CtaBanner />
        </Layout>
    )
}

export const getStaticProps: GetStaticProps<HomeProps> = async () => {
    const [services, gallery, testimonials, clients] = await Promise.all([
        safely('home services', () => getServices({ featuredOnly: true }), []),
        safely('home gallery', () => getGalleryItems({ featuredOnly: true, limit: 8 }), []),
        safely('home testimonials', () => getTestimonials({ featuredOnly: true }), []),
        safely('home clients', () => getClients(), [])
    ])

    return {
        props: {
            services,
            gallery,
            testimonials,
            clients
        },
        revalidate: 60
    }
}

export default Home
